import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

function DepartmentForm() {
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState({ name: '', password: '', is_on_call_viewer: false, is_global_viewer: false });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [resettingId, setResettingId] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API_URL}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setMessage({ text: 'Error loading departments', type: 'error' });
    }
  };

  const usernamePreview = formData.name.toLowerCase().replace(/[^a-z0-9]/g, '') || null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setMessage({ text: 'Department name is required', type: 'error' });
      return;
    }
    if (!formData.password.trim()) {
      setMessage({ text: 'Admin password is required', type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/departments`, formData);
      await fetchDepartments();
      setFormData({ name: '', password: '', is_on_call_viewer: false, is_global_viewer: false });

      const username = response.data.admin_credentials?.username;
      setMessage({
        text: username
          ? `Department created. Login: ${username} / ${formData.password}`
          : 'Department created successfully!',
        type: 'success'
      });
      setTimeout(() => setMessage(''), 10000);
    } catch (error) {
      const errText = error.response?.data?.error || 'Error creating department';
      setMessage({ text: errText, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (dept) => {
    let timeslotCount = 0;
    let signupCount = 0;
    try {
      // Use the uncached admin endpoint so the count reflects recent edits.
      const res = await axios.get(`${API_URL}/timeslots/admin/data`);
      const deptSlots = res.data.timeslots.filter(s => s.department_id === dept.id);
      timeslotCount = deptSlots.length;
      signupCount = deptSlots.reduce((sum, s) => sum + (s.current_signups || 0), 0);
    } catch (e) {
      // Soft-fail: still confirm but without counts
    }

    let prompt = `Delete department "${dept.name}"?`;
    if (timeslotCount > 0 || signupCount > 0) {
      prompt = `Delete "${dept.name}"? This will also remove ` +
        `${timeslotCount} timeslot${timeslotCount !== 1 ? 's' : ''} ` +
        `and ${signupCount} volunteer signup${signupCount !== 1 ? 's' : ''}, plus the department admin account. ` +
        `This cannot be undone.`;
    }

    if (!window.confirm(prompt)) return;
    try {
      await axios.delete(`${API_URL}/departments/${dept.id}`);
      setDepartments(departments.filter(d => d.id !== dept.id));
      setMessage({ text: 'Department deleted successfully!', type: 'success' });
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage({ text: 'Error deleting department', type: 'error' });
    }
  };

  const toggleOnCallViewer = async (dept) => {
    const next = !dept.is_on_call_viewer;
    try {
      const res = await axios.put(`${API_URL}/departments/${dept.id}`, {
        is_on_call_viewer: next
      });
      setDepartments(departments.map(d => d.id === dept.id ? res.data : d));
      setMessage({
        text: next
          ? `${dept.name} can now view on-call shifts from other departments.`
          : `${dept.name} no longer views on-call shifts from other departments.`,
        type: 'success'
      });
      setTimeout(() => setMessage(''), 4000);
    } catch (error) {
      setMessage({ text: 'Error updating department', type: 'error' });
    }
  };

  const toggleGlobalViewer = async (dept) => {
    const next = !dept.is_global_viewer;
    try {
      const res = await axios.put(`${API_URL}/departments/${dept.id}`, {
        is_global_viewer: next
      });
      setDepartments(departments.map(d => d.id === dept.id ? res.data : d));
      setMessage({
        text: next
          ? `${dept.name} can now view all departments' shifts (read-only).`
          : `${dept.name} no longer views other departments' shifts.`,
        type: 'success'
      });
      setTimeout(() => setMessage(''), 4000);
    } catch (error) {
      setMessage({ text: 'Error updating department', type: 'error' });
    }
  };

  const handleResetPassword = async (deptId) => {
    if (!resetPassword.trim()) {
      setMessage({ text: 'New password cannot be empty', type: 'error' });
      return;
    }
    setResetLoading(true);
    try {
      await axios.put(`${API_URL}/departments/${deptId}/reset-password`, {
        password: resetPassword.trim()
      });
      setResettingId(null);
      setResetPassword('');
      setMessage({ text: 'Password reset successfully!', type: 'success' });
      setTimeout(() => setMessage(''), 4000);
    } catch (error) {
      const errText = error.response?.data?.error || 'Error resetting password';
      setMessage({ text: errText, type: 'error' });
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="form-container">
      <h2>Manage Departments</h2>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Department Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Enter department name"
            disabled={loading}
          />
          {usernamePreview && (
            <span className="username-preview">Login username will be: <strong>{usernamePreview}</strong></span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="password">Admin Password</label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={e => setFormData(prev => ({ ...prev, password: e.target.value }))}
            placeholder="Set initial admin password"
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label className="tsm-checkbox-label">
            <input
              type="checkbox"
              checked={!!formData.is_on_call_viewer}
              onChange={e => setFormData(prev => ({ ...prev, is_on_call_viewer: e.target.checked }))}
              disabled={loading}
            />
            <span>On-call viewer (can read other depts' on-call shifts)</span>
          </label>
        </div>

        <div className="form-group">
          <label className="tsm-checkbox-label">
            <input
              type="checkbox"
              checked={!!formData.is_global_viewer}
              onChange={e => setFormData(prev => ({ ...prev, is_global_viewer: e.target.checked }))}
              disabled={loading}
            />
            <span>Global viewer (can read every department's shifts, read-only)</span>
          </label>
        </div>

        <button type="submit" className="submit-btn" disabled={loading}>
          {loading ? 'Creating...' : 'Add Department'}
        </button>
      </form>

      <div className="list-container">
        <h3>Departments</h3>
        {departments.length === 0 ? (
          <p>No departments yet.</p>
        ) : (
          [...departments].sort((a, b) => a.name.localeCompare(b.name)).map(dept => (
            <div key={dept.id} className="list-item">
              <div className="list-item-content">
                <h4>{dept.name}</h4>
                {dept.assigned_admin && (
                  <p>Admin login: <strong>{dept.assigned_admin}</strong></p>
                )}
                {dept.is_on_call_viewer && (
                  <p><em>On-call viewer — can read on-call shifts from other depts</em></p>
                )}
                {dept.is_global_viewer && (
                  <p><em>Global viewer — can read all departments' shifts (read-only)</em></p>
                )}

                {resettingId === dept.id && (
                  <div className="reset-password-form">
                    <input
                      type="password"
                      placeholder="New password"
                      value={resetPassword}
                      onChange={e => setResetPassword(e.target.value)}
                      className="reset-password-input"
                      autoFocus
                    />
                    <div className="reset-password-actions">
                      <button
                        className="edit-btn"
                        onClick={() => handleResetPassword(dept.id)}
                        disabled={resetLoading}
                      >
                        {resetLoading ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className="cancel-btn"
                        onClick={() => { setResettingId(null); setResetPassword(''); }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="list-item-actions">
                {resettingId !== dept.id && (
                  <>
                    <button
                      className="edit-btn"
                      onClick={() => toggleOnCallViewer(dept)}
                    >
                      {dept.is_on_call_viewer ? 'Disable On-Call View' : 'Enable On-Call View'}
                    </button>
                    <button
                      className="edit-btn"
                      onClick={() => toggleGlobalViewer(dept)}
                    >
                      {dept.is_global_viewer ? 'Disable Global View' : 'Enable Global View'}
                    </button>
                    <button
                      className="edit-btn"
                      onClick={() => { setResettingId(dept.id); setResetPassword(''); }}
                    >
                      Reset Password
                    </button>
                  </>
                )}
                <button
                  className="delete-btn"
                  onClick={() => handleDelete(dept)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default DepartmentForm;
